import { Container, Divider } from "@material-ui/core";
import { Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  CurrencyDisplay,
  CurrencyNumberInput,
  CustomTable,
  FormCard,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
} from "components";
import { HeaderTitle } from "layouts";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import { optionsStatusAdvanceComposer } from "./Components/Constants";

const DetailAdvancedComposer = () => {
  const classes = globalStyles();
  const history = useHistory();
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loadingPage, setLoadingPage] = useState(true);
  const [pageDetail, setPageDetail] = useState(null);
  const [dataTable, setDataTable] = useState([]);
  const isStatusOpen = pageDetail?.status === "open";
  const handleChangeSettlementValue = value => {
    console.log("Settlement Value Changed: ", value);
    setDataTable(prev =>
      prev.map((item, index) =>
        index === 0 ? { ...item, manual_settlement: value } : item
      )
    );
  };
  const getDetail = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/advance-composer/v2/detail/${id}`,
        { headers }
      );
      const { data } = res?.data;
      const {
        song_title,
        composer_name,
        publisher_share_percentage,
        ownership_percentage,
        iswc,
        advance_value,
        settlement_amount,
        unsettled_amount,
      } = data?.song;
      setPageDetail(data);
      setDataTable([
        {
          song_title,
          composer_name,
          publisher_share_percentage,
          ownership_percentage,
          iswc,
          advance_value,
          settlement_amount,
          unsettled_amount,
          manual_settlement: 0,
        },
      ]);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    try {
      setLoadingPage(true);
      await axios.put(
        `${hardBaseUrl}/advance-composer/v2/settle/${id}`,
        {},
        {
          headers,
          params: { amount: dataTable?.[0].manual_settlement },
        }
      );
      ModalSuccess("Successfully settled advance").then(() => history.goBack());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    getDetail();
  }, []);

  return (
    <Page className={classes.root} title="Composer/Author Advance Detail">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title="Composer/Author Advance Detail"
            breadcrumbData={breadcrumbData}
            backButton
          />
          <Divider className={classes.divider} />
          <SectionLabel
            title="Composer/Author Advance Detail"
            subTitle="This is Composer/Author Advance Detail information"
          />
          <SectionLabel
            title="Song Contract Information"
            subTitle="Contract information that has been made"
            my={2}
          />
          <FormCard>
            <Grid container spacing={2} mb={2}>
              {formPageDetail?.map(({ label, value, type }, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Typography>{`${label}:`}</Typography>
                  {type === "chip" ? (
                    <StatusChip
                      label={
                        optionsStatusAdvanceComposer?.find(
                          item => item?.value === pageDetail?.[value]
                        )?.name || "-"
                      }
                      type={
                        optionsStatusAdvanceComposer?.find(
                          item => item?.value === pageDetail?.[value]
                        )?.color || ""
                      }
                    />
                  ) : (
                    <SectionLabel title={pageDetail?.[value] || "-"} />
                  )}
                </Grid>
              ))}
            </Grid>
          </FormCard>
          <SectionLabel
            title="Song Information"
            subTitle="Detail song information"
            my={2}
          />
          <CustomTable columnTable={columnTable()} data={dataTable} />
          <SectionLabel
            title="Advance value"
            subTitle="Feature to set Advance Payment"
            my={2}
          />
          <CustomTable
            columnTable={columnTable(handleChangeSettlementValue, isStatusOpen)}
            data={dataTable}
          />
          {isStatusOpen && (
            <Grid container justifyContent="right" my={2}>
              <PrimaryButton onClick={handleSubmit} label="Submit" />
            </Grid>
          )}
        </Container>
      )}
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/advance",
  },
  {
    label: "Composer/Author Advance",
    link: "/admin/advance",
  },
  {
    label: "Composer/Author Advance Detail",
    active: true,
  },
];
const formPageDetail = [
  { label: "Contract Number", value: "contract_number" },
  { label: "Contract Status", value: "status", type: "chip" },
  { label: "Start Date", value: "contract_start_date" },
  { label: "End Date", value: "contract_end_date" },
  { label: "Publisher", value: "publisher_name" },
  { label: "Publisher ID", value: "publisher_id" },
  { label: "Description", value: "contract_description" },
];
const columnTable = (onChange, manualSettlement) => [
  {
    name: "song_title",
    title: "Song",
  },
  {
    name: "composer_name",
    title: "Composer/Author Name",
  },
  ...(onChange
    ? [
        {
          name: "advance_value",
          title: "Advance Value",
          headerAlign: "right",
          renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
        },
        {
          name: "settlement_amount",
          title: "Settled Amount",
          headerAlign: "right",
          renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
        },
        {
          name: "unsettled_amount",
          title: "Unsettled Amount",
          headerAlign: "right",
          renderText: item => <CurrencyDisplay value={item} prefix="Rp" />,
        },
        ...(manualSettlement
          ? [
              {
                name: "manual_settlement",
                title: "Manual Settlement",
                renderText: item => (
                  <CurrencyNumberInput
                    value={item}
                    onChange={onChange}
                    decimalScale={2}
                    startAdornment="Rp"
                  />
                ),
              },
            ]
          : []),
      ]
    : [
        {
          name: "publisher_share_percentage",
          title: "Publisher Share Percentage",
          renderText: item => `${item}%`,
        },
        {
          name: "ownership_percentage",
          title: "Ownership Share Percentage",
          renderText: item => `${item}%`,
        },
        {
          name: "iswc",
          title: "ISWC Code",
        },
      ]),
];

export default DetailAdvancedComposer;
